<template>
  <div class="chat-view">
    <div class="chat-container">
      <div class="chat-messages" ref="messagesContainer">
        <div v-if="messages.length === 0" class="welcome-message">
          <div class="card welcome-card">
            <!-- <h2>Welcome to STEM Tutor GenAI Agent</h2>
            <p>Upload data files, ask questions, and get insights from your data. I'm here to help!</p> -->
            <p>Ask any STEM question! See the suggestions below:</p>
            <div class="welcome-suggestions">
              <button 
                v-for="(suggestion, index) in welcomeSuggestions" 
                :key="index" 
                class="btn suggestion-btn"
                @click="sendMessage(suggestion)">
                {{ suggestion }}
              </button>
            </div>
          </div>
        </div>
        <ChatMessage 
          v-for="message in messages" 
          :key="message.id" 
          :message="message"
          @give-feedback="provideFeedback" />
        
        <!-- Thinking indicator -->
        <div v-if="isLoading" class="thinking-indicator">
          <div class="thinking-avatar">
            <span class="material-icons">smart_toy</span>
          </div>
          <div class="thinking-content">
            <div class="thinking-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
            <span class="thinking-text">Thinking...</span>
          </div>
        </div>
      </div>
      
      <div class="chat-input-container">
        <div class="upload-options">
          <button class="btn upload-btn" @click="$refs.fileInput.click()">
            <span class="material-icons">attach_file</span>
          </button>
          <input 
            type="file" 
            ref="fileInput" 
            @change="handleFileUpload" 
            style="display: none"
            accept=".png,.jpg,.jpeg,.csv,.xlsx,.xls,.json" />
        </div>
        
        <textarea 
          v-model="userInput" 
          class="chat-input" 
          placeholder="Type your message here..."
          @keydown.enter.prevent="sendMessage(userInput)"
          rows="1"
          ref="inputField"></textarea>
        
        <button 
          class="btn send-btn" 
          @click="sendMessage(userInput)"
          :disabled="isLoading || !userInput.trim()">
          <span class="material-icons">send</span>
        </button>
      </div>
    </div>
    
    <!-- <div class="chat-sidebar">
      <div class="card upload-area" @dragover.prevent @drop.prevent="handleFileDrop">
        <div class="upload-title">
          <span class="material-icons">cloud_upload</span>
          <h3>Upload Files</h3>
        </div>
        <p>Drag & drop files here or click below</p>
        <button class="btn btn-primary" @click="$refs.fileInput.click()">
          Select Files
        </button>
      </div>
      
      <div class="card uploaded-files" v-if="uploadedFiles.length > 0">
        <h3>Uploaded Files</h3>
        <ul class="file-list">
          <li v-for="file in uploadedFiles" :key="file.id" class="file-item">
            <span class="material-icons file-icon">
              {{ getFileIcon(file.type) }}
            </span>
            <div class="file-info">
              <span class="file-name">{{ file.name }}</span>
              <span class="file-date">{{ file.date }}</span>
            </div>
          </li>
        </ul>
      </div>
    </div> -->
  </div>
</template>

<script>
import ChatMessage from '@/components/ChatMessage.vue'
import apiService from '@/services/api'
import { generateId, formatDateTime, getFileType, getCurrentUser } from '@/utils/helpers'

export default {
  name: 'ChatView',
  components: {
    ChatMessage
  },
  data() {
    return {
      messages: [],
      userInput: '',
      isLoading: false,
      userId: null,
      context: {},
      uploadedFiles: [],
      welcomeSuggestions: [
        "What is the area of an equilateral triangle with side length 5cm?",
        "Electrons in a Sodium atom emit radiation when they move from a higher energy level of -0.81eV to a lower energy level of -2.21eV. What is the frequency of radiation emitted in the process?",
        "What is the differences between aerobic and anaerobic respiration?",
      ]
    }
  },
  created() {
    // Get session data
    const sessionStr = localStorage.getItem('session')
    const session = sessionStr ? JSON.parse(sessionStr) : null
    
    if (session) {
      this.userId = session.userId
    } else {
      // Fallback to generated ID
      this.userId = getCurrentUser()
    }
    
    // Check if the API is available
    apiService.checkHealth()
      .then(response => {
        console.log('API Status:', response.data)
      })
      .catch(error => {
        console.error('API Error:', error)
        this.addSystemMessage('Error connecting to the server. Please try again later.')
      })
  },
  mounted() {
    this.$refs.inputField.addEventListener('input', this.autoResize)
  },
  beforeUnmount() {
    this.$refs.inputField.removeEventListener('input', this.autoResize)
  },
  methods: {
    sendMessage(content) {
      if (!content.trim()) return
      
      // Reset the input field
      if (content === this.userInput) {
        this.userInput = ''
      }
      
      // Create a new user message
      const messageId = generateId()
      this.addMessage({
        id: messageId,
        role: 'user',
        content: content,
        timestamp: new Date().toISOString()
      })
      
      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom()
      })
      
      // Format messages for API
      const apiMessages = this.messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }))
      
      // Send to API
      this.isLoading = true
      
      // Scroll to show thinking indicator
      this.$nextTick(() => {
        this.scrollToBottom()
      })
      
      apiService.sendMessage(apiMessages, this.context, this.userId)
        .then(response => {
          // Add the agent's response
          this.addMessage({
            id: generateId(),
            role: 'assistant',
            content: response.data.reply,
            timestamp: new Date().toISOString(),
            replyTo: messageId
          })
          
          // Update context
          if (response.data.context) {
            this.context = response.data.context
          }
        })
        .catch(error => {
          console.error('Message Error:', error)
          this.addSystemMessage('Error sending message. Please try again.')
        })
        .finally(() => {
          this.isLoading = false
          this.scrollToBottom()
        })
    },
    
    addMessage(message) {
      this.messages.push(message)
    },
    
    addSystemMessage(content) {
      this.addMessage({
        id: generateId(),
        role: 'system',
        content: content,
        timestamp: new Date().toISOString()
      })
    },
    
    scrollToBottom() {
      const container = this.$refs.messagesContainer
      container.scrollTop = container.scrollHeight
    },
    
    autoResize(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      
      this.uploadFile(file)
      event.target.value = null // Reset the input
    },
    
    handleFileDrop(event) {
      const file = event.dataTransfer.files[0]
      if (!file) return
      
      this.uploadFile(file)
    },
    
    uploadFile(file) {
      const fileType = getFileType(file.name)
      
      this.addSystemMessage(`Uploading file: ${file.name}`)
      this.isLoading = true
      
      let uploadPromise
      
      switch (fileType) {
        case 'image':
          uploadPromise = apiService.uploadSnapshot(file)
          break
        case 'data':
          uploadPromise = apiService.uploadDataFile(file)
          break
        case 'json':
          // For JSON files, we need to read the content first
          uploadPromise = new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = e => {
              try {
                const jsonData = JSON.parse(e.target.result)
                apiService.uploadJsonData(jsonData)
                  .then(resolve)
                  .catch(reject)
              } catch (err) {
                reject(new Error('Invalid JSON file'))
              }
            }
            reader.onerror = reject
            reader.readAsText(file)
          })
          break
        default:
          this.addSystemMessage('Unsupported file type.')
          this.isLoading = false
          return
      }
      
      uploadPromise
        .then(response => {
          this.addSystemMessage(`File uploaded successfully: ${file.name}`)
          
          // Add to uploaded files list
          this.uploadedFiles.push({
            id: generateId(),
            name: file.name,
            type: fileType,
            date: formatDateTime(new Date()),
            url: response.data.url || null
          })
          
          // If it's a data file with a preview, show it
          if (response.data.preview) {
            this.addMessage({
              id: generateId(),
              role: 'assistant',
              content: `I've analyzed your file "${file.name}". It contains ${response.data.rows} rows of data. Here's a preview of the first few rows:`,
              timestamp: new Date().toISOString(),
              preview: response.data.preview
            })
          }
        })
        .catch(error => {
          console.error('Upload Error:', error)
          this.addSystemMessage(`Error uploading file: ${error.message || 'Unknown error'}`)
        })
        .finally(() => {
          this.isLoading = false
          this.scrollToBottom()
        })
    },
    
    provideFeedback(messageId, feedback) {
      apiService.sendFeedback(this.userId, messageId, feedback.thumb, feedback.comment)
        .then(() => {
          console.log('Feedback sent')
        })
        .catch(error => {
          console.error('Feedback Error:', error)
        })
    },
    
    getFileIcon(fileType) {
      switch (fileType) {
        case 'image': return 'image'
        case 'data': return 'table_chart'
        case 'json': return 'data_object'
        default: return 'insert_drive_file'
      }
    }
  }
}
</script>

<style scoped>
.chat-view {
  display: flex;
  gap: 24px;
  height: calc(100vh - 130px);
}

.chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 8px;
  background-color: var(--background-card);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.chat-input-container {
  display: flex;
  align-items: flex-end;
  padding: 12px;
  background-color: var(--background-dark);
  border-top: 1px solid var(--border-color);
}

.chat-input {
  flex: 1;
  resize: none;
  min-height: 40px;
  max-height: 120px;
  padding: 10px 12px;
  border-radius: 20px;
  margin: 0 10px;
  background-color: var(--background-dark);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.upload-btn, .send-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-dark);
  color: var(--text-on-primary);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chat-sidebar {
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.upload-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  text-align: center;
  border: 2px dashed var(--border-color);
  border-radius: 8px;
}

.upload-title {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.upload-title .material-icons {
  margin-right: 8px;
  color: var(--primary-color);
}

.uploaded-files {
  flex: 1;
  overflow-y: auto;
}

.file-list {
  list-style: none;
  margin-top: 12px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 8px;
  border-bottom: 1px solid var(--border-color);
}

.file-icon {
  margin-right: 12px;
  color: var(--primary-color);
}

.file-info {
  display: flex;
  flex-direction: column;
}

.file-name {
  font-weight: 500;
}

.file-date {
  font-size: 12px;
  color: var(--text-secondary);
}

.welcome-message {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.welcome-card {
  max-width: 600px;
  text-align: center;
  padding: 24px;
}

.welcome-card h2 {
  color: var(--primary-light);
  margin-bottom: 16px;
}

.welcome-suggestions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
}

.suggestion-btn {
  background-color: var(--background-dark);
  color: var(--primary-light);
  border: 1px solid var(--primary-dark);
  border-radius: 16px;
  padding: 8px 16px;
}

.suggestion-btn:hover {
  background-color: var(--primary-dark);
  color: var(--text-on-primary);
}

.thinking-indicator {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  margin: 8px 0;
  gap: 12px;
}

.thinking-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--primary-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.thinking-avatar .material-icons {
  color: var(--text-on-primary);
  font-size: 24px;
}

.thinking-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}

.thinking-dots {
  display: flex;
  gap: 6px;
}

.thinking-dots .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--primary-light);
  animation: thinking-pulse 1.4s ease-in-out infinite;
}

.thinking-dots .dot:nth-child(1) {
  animation-delay: 0s;
}

.thinking-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.thinking-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

.thinking-text {
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
}

@keyframes thinking-pulse {
  0%, 60%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  30% {
    opacity: 1;
    transform: scale(1.1);
  }
}

@media (max-width: 768px) {
  .chat-view {
    flex-direction: column;
  }
  
  .chat-container {
    height: calc(100vh - 250px);
  }
  
  .chat-sidebar {
    width: 100%;
    height: auto;
  }
}
</style>
